import React, { useEffect, useMemo, useRef } from 'react';
import { NETSUITE_FIXED } from '../config';
import {
  NetSuiteBillItem,
  NetSuiteBillPayload,
  NetSuiteBillTextField,
  NetSuiteUnresolvedNames,
  NetSuiteVendorCandidate,
} from '../types';
import {
  BillErrors,
  ExtractedTotals,
  emptyBillItem,
  formatMoney,
  lineAmount,
  linesTotal,
  newExternalId,
  rememberedVendorId,
} from '../utils/netsuite';

interface NetSuiteBillFormProps {
  payload: NetSuiteBillPayload;
  onChange: (next: NetSuiteBillPayload) => void;
  /** Errors to display: local checks after the first submit attempt, plus names NetSuite couldn't match. */
  errors: BillErrors;
  disabled: boolean;
  extracted: ExtractedTotals;
  /** NetSuite's answer when names didn't match exactly one record — offers its candidates. */
  unresolved: NetSuiteUnresolvedNames | null;
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  hint?: React.ReactNode;
  error?: string;
  /** Small control rendered beside the label (e.g. "New ID"). */
  action?: React.ReactNode;
  multiline?: boolean;
  wide?: boolean;
  mono?: boolean;
}

const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  value,
  onChange,
  required,
  placeholder,
  hint,
  error,
  action,
  multiline,
  wide,
  mono,
}) => {
  const common = {
    id,
    className: `input nsb__input${mono ? ' nsb__input--mono' : ''}${error ? ' is-invalid' : ''}`,
    value,
    placeholder,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-error` : hint ? `${id}-hint` : undefined,
    'aria-required': required || undefined,
  };

  return (
    <div className={`nsb__field${wide ? ' nsb__field--wide' : ''}`}>
      <div className="nsb__label-row">
        <label className="nsb__label" htmlFor={id}>
          {label}
          {required && (
            <span className="nsb__req" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {action}
      </div>
      {multiline ? (
        <textarea {...common} rows={2} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...common} type="text" autoComplete="off" onChange={(e) => onChange(e.target.value)} />
      )}
      {error ? (
        <p className="nsb__error" id={`${id}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="nsb__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

interface LineCellProps {
  id: string;
  label: string;
  error?: string;
  className: string;
  children: React.ReactElement;
}

/** One cell of a line row. Labels are visible on phones, screen-reader-only on wider screens. */
const LineCell: React.FC<LineCellProps> = ({ id, label, error, className, children }) => (
  <div className={`nsb__cell ${className}`}>
    <label className="nsb__cell-label" htmlFor={id}>
      {label}
    </label>
    {children}
    {error && (
      <p className="nsb__error" id={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

type VendorIssue = NonNullable<NetSuiteUnresolvedNames['vendor']>;

/** NetSuite found several vendors with the invoice's name: the user picks one. */
const VendorPicker: React.FC<{
  issue: VendorIssue;
  selectedId: string;
  onPick: (candidate: NetSuiteVendorCandidate) => void;
}> = ({ issue, selectedId, onPick }) => (
  <fieldset className="nsb__picker" id="nsb-vendor-picker">
    <legend className="nsb__picker-title">{issue.message}</legend>
    <div className="nsb__candidates">
      {(issue.candidates || []).map((candidate) => {
        const id = String(candidate.vendorId);
        const selected = selectedId === id;
        const meta: [string, string | null | undefined][] = [
          ['Subsidiary', candidate.subsidiary],
          ['Currency', candidate.currency],
          ['VAT no.', candidate.vatNumber],
          ['Reg. no.', candidate.registrationNumber],
          ['Address', candidate.address],
        ];
        return (
          <label key={id} className={`nsb__candidate${selected ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="nsb-vendor-candidate"
              className="nsb__candidate-input"
              checked={selected}
              onChange={() => onPick(candidate)}
            />
            <span className="nsb__candidate-head">
              <span className="nsb__candidate-name">{candidate.name || issue.vendorName}</span>
              <span className="nsb__candidate-ids">
                {candidate.entityId ? `${candidate.entityId} · ` : ''}ID {id}
              </span>
            </span>
            <span className="nsb__candidate-meta">
              {meta
                .filter((entry): entry is [string, string] => Boolean(entry[1]))
                .map(([label, value]) => (
                  <span key={label} className="nsb__candidate-row">
                    <span className="nsb__candidate-label">{label}</span>
                    {value}
                  </span>
                ))}
            </span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

/** Item candidates NetSuite offered for one line, in whatever shape it sent them. */
function itemCandidates(unresolved: NetSuiteUnresolvedNames | null, index: number) {
  const issue = unresolved?.items?.find((item) => item.index === index);
  return (issue?.candidates || []).flatMap((c) => {
    const id = c.itemId ?? c.id;
    if (id === undefined || id === null) return [];
    const name = typeof c.name === 'string' ? c.name : typeof c.itemName === 'string' ? c.itemName : '';
    return [{ itemId: String(id), name }];
  });
}

// A number box that is empty is stored as NaN (serialised as null) so a
// missing rate can never be mistaken for a real 0.
const numberValue = (n: number) => (Number.isFinite(n) ? n : '');
const readNumber = (raw: string) => (raw === '' ? NaN : Number(raw));

const NetSuiteBillForm: React.FC<NetSuiteBillFormProps> = ({
  payload,
  onChange,
  errors,
  disabled,
  extracted,
  unresolved,
}) => {
  const { items } = payload;
  // The field the user can edit wins over what the document happened to say.
  const currency = (typeof payload.currency === 'string' && payload.currency) || extracted.currency;
  const total = linesTotal(items);

  // Focus the first box of a freshly added line.
  const focusLine = useRef<number | null>(null);
  useEffect(() => {
    if (focusLine.current === null) return;
    document.getElementById(`nsb-line-${focusLine.current}-itemId`)?.focus();
    focusLine.current = null;
  }, [items.length]);

  const remembered = useMemo(() => rememberedVendorId(payload.vendorName), [payload.vendorName]);

  // currency is optional on the payload, so the value is guarded into a string.
  const field = (name: NetSuiteBillTextField) => ({
    id: `nsb-${name}`,
    value: typeof payload[name] === 'string' ? (payload[name] as string) : '',
    error: errors[name],
    onChange: (value: string) => onChange({ ...payload, [name]: value }),
  });

  // vendorId is only part of the request once it has a value.
  const vendorId = payload.vendorId === undefined || payload.vendorId === null ? '' : String(payload.vendorId);
  const setVendorId = (value: string) => {
    const next: NetSuiteBillPayload = { ...payload, vendorId: value };
    if (!value.trim()) delete next.vendorId;
    onChange(next);
  };
  const pickVendor = (candidate: NetSuiteVendorCandidate) =>
    onChange({
      ...payload,
      vendorId: String(candidate.vendorId),
      vendorEntityId: candidate.entityId || payload.vendorEntityId,
    });
  const vendorIssue = unresolved?.vendor;
  const hasVendorCandidates = !!vendorIssue?.candidates?.length;

  const setItems = (next: NetSuiteBillItem[]) => onChange({ ...payload, items: next });
  const updateItem = (index: number, patch: Partial<NetSuiteBillItem>) =>
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const addLine = () => {
    focusLine.current = items.length;
    setItems([...items, emptyBillItem()]);
  };
  const removeLine = (index: number) => setItems(items.filter((_, i) => i !== index));
  const firstItem = items[0];
  const canCopyFirstItem =
    items.length > 1 && !!firstItem && !!(firstItem.itemId.trim() || firstItem.itemName.trim());
  const copyFirstItemToAll = () =>
    setItems(items.map((item) => ({ ...item, itemId: firstItem.itemId, itemName: firstItem.itemName })));

  // Compare against the net figure when the document shows one: bill lines are net rates.
  const reference = Number.isFinite(extracted.subtotal)
    ? { label: 'invoice net total', value: extracted.subtotal }
    : Number.isFinite(extracted.total)
      ? { label: 'invoice total', value: extracted.total }
      : null;
  let comparison: React.ReactNode = null;
  if (reference && items.length > 0) {
    if (!Number.isFinite(total)) {
      comparison = <span className="nsb__match">Fill in every quantity and rate to compare</span>;
    } else if (Math.abs(total - reference.value) < 0.01) {
      comparison = (
        <span className="nsb__match nsb__match--ok">
          <span aria-hidden="true">✓</span> Matches the {reference.label}
        </span>
      );
    } else {
      comparison = (
        <span className="nsb__match nsb__match--warn">
          <span aria-hidden="true">!</span> {formatMoney(total - reference.value, currency)} vs the{' '}
          {reference.label} of {formatMoney(reference.value, currency)}
        </span>
      );
    }
  }

  return (
    <fieldset className="nsb__form" disabled={disabled}>
      <legend className="nsb__sr-only">NetSuite bill details</legend>

      <section className="nsb__section" aria-labelledby="nsb-sec-vendor">
        <h3 className="nsb__section-title" id="nsb-sec-vendor">
          <span className="nsb__section-icon" aria-hidden="true">
            🏢
          </span>
          Vendor
        </h3>
        <div className="nsb__grid">
          <TextField
            {...field('vendorName')}
            label="Vendor name"
            placeholder="As printed on the invoice"
            wide
          />
          <TextField
            {...field('vendorEntityId')}
            label="Vendor entity ID"
            placeholder="e.g. VEN12"
            mono
            hint={
              remembered && remembered === payload.vendorEntityId ? (
                <span className="nsb__hint--remembered">✓ Remembered from a previous bill for this vendor</span>
              ) : (
                'Enter this, the name, or both.'
              )
            }
          />
          <TextField
            id="nsb-vendorId"
            label="Vendor internal ID"
            value={vendorId}
            onChange={setVendorId}
            placeholder="e.g. 463"
            mono
            hint="Set when you choose one of NetSuite's suggested vendors."
          />
        </div>
        {vendorIssue && hasVendorCandidates && (
          <VendorPicker issue={vendorIssue} selectedId={vendorId} onPick={pickVendor} />
        )}
      </section>

      <section className="nsb__section" aria-labelledby="nsb-sec-bill">
        <h3 className="nsb__section-title" id="nsb-sec-bill">
          <span className="nsb__section-icon" aria-hidden="true">
            🧾
          </span>
          Bill details
        </h3>
        <div className="nsb__grid">
          <TextField {...field('vendorInvoiceNumber')} label="Vendor invoice number" required mono />
          <TextField
            {...field('purchaseOrderNumber')}
            label="Purchase order number"
            placeholder="e.g. PO2500025"
            hint="Optional — leave empty if the bill has no PO."
            mono
          />
          <TextField
            {...field('currency')}
            label="Currency"
            placeholder="e.g. CZK"
            mono
            hint="ISO code read off the bill. Empty leaves it to NetSuite."
          />
          <TextField
            {...field('externalId')}
            label="External ID"
            mono
            wide
            hint="Never reused: every attempt to create the bill sends a new one."
            action={
              <button
                type="button"
                className="link-btn nsb__label-action"
                onClick={() => onChange({ ...payload, externalId: newExternalId(payload.vendorInvoiceNumber) })}
                title="Generate a new external ID"
              >
                ↻ New ID
              </button>
            }
          />
          <TextField {...field('memo')} label="Memo" multiline wide />
        </div>
        <div className="nsb__fixed">
          <span className="nsb__fixed-chip">
            <span className="nsb__fixed-label">Subsidiary</span>
            <code>{NETSUITE_FIXED.subsidiaryId}</code>
          </span>
          <span className="nsb__fixed-chip">
            <span className="nsb__fixed-label">Requestor</span>
            <code>{NETSUITE_FIXED.requestorId}</code>
          </span>
          <span className="nsb__fixed-note">
            <span aria-hidden="true">🔒</span> Fixed for every bill
          </span>
        </div>
      </section>

      <section className="nsb__section" aria-labelledby="nsb-sec-lines">
        <div className="nsb__section-head">
          <h3 className="nsb__section-title" id="nsb-sec-lines">
            <span className="nsb__section-icon" aria-hidden="true">
              📋
            </span>
            Line items <span className="nsb__count">{items.length}</span>
          </h3>
          {canCopyFirstItem && (
            <button type="button" className="link-btn" onClick={copyFirstItemToAll}>
              Use line 1's item on every line
            </button>
          )}
        </div>

        {items.length > 0 ? (
          <>
            <div className="nsb__lines-header" aria-hidden="true">
              <span>#</span>
              <span>Item ID</span>
              <span>Item name</span>
              <span className="nsb__num">Qty</span>
              <span className="nsb__num">Rate</span>
              <span className="nsb__num">Amount</span>
              <span />
            </div>
            <ol className="nsb__lines">
              {items.map((item, i) => {
                const id = (name: string) => `nsb-line-${i}-${name}`;
                const err = (name: string) => errors[`items.${i}.${name}`];
                const describe = (name: string) => (err(name) ? `${id(name)}-error` : undefined);
                const suggestions = itemCandidates(unresolved, i);
                return (
                  <li className="nsb__line" key={i}>
                    <span className="nsb__line-no" aria-hidden="true">
                      {i + 1}
                    </span>
                    <LineCell id={id('itemId')} label="Item ID" error={err('itemId')} className="nsb__cell--itemId">
                      <input
                        id={id('itemId')}
                        className={`input nsb__input nsb__input--mono${err('itemId') ? ' is-invalid' : ''}`}
                        value={item.itemId}
                        placeholder="e.g. 186"
                        autoComplete="off"
                        aria-invalid={err('itemId') ? true : undefined}
                        aria-describedby={describe('itemId')}
                        onChange={(e) => updateItem(i, { itemId: e.target.value })}
                      />
                    </LineCell>
                    <LineCell
                      id={id('itemName')}
                      label="Item name"
                      error={err('itemName')}
                      className="nsb__cell--itemName"
                    >
                      <input
                        id={id('itemName')}
                        className={`input nsb__input${err('itemName') ? ' is-invalid' : ''}`}
                        value={item.itemName}
                        placeholder="or item name"
                        autoComplete="off"
                        aria-invalid={err('itemName') ? true : undefined}
                        aria-describedby={describe('itemName')}
                        onChange={(e) => updateItem(i, { itemName: e.target.value })}
                      />
                    </LineCell>
                    <LineCell id={id('quantity')} label="Qty" error={err('quantity')} className="nsb__cell--qty">
                      <input
                        id={id('quantity')}
                        className={`input nsb__input nsb__num${err('quantity') ? ' is-invalid' : ''}`}
                        type="number"
                        inputMode="decimal"
                        step="any"
                        value={numberValue(item.quantity)}
                        aria-invalid={err('quantity') ? true : undefined}
                        aria-describedby={describe('quantity')}
                        onChange={(e) => updateItem(i, { quantity: readNumber(e.target.value) })}
                      />
                    </LineCell>
                    <LineCell id={id('rate')} label="Rate" error={err('rate')} className="nsb__cell--rate">
                      <input
                        id={id('rate')}
                        className={`input nsb__input nsb__num${err('rate') ? ' is-invalid' : ''}`}
                        type="number"
                        inputMode="decimal"
                        step="any"
                        value={numberValue(item.rate)}
                        placeholder="0.00"
                        aria-invalid={err('rate') ? true : undefined}
                        aria-describedby={describe('rate')}
                        onChange={(e) => updateItem(i, { rate: readNumber(e.target.value) })}
                      />
                    </LineCell>
                    <div className="nsb__cell nsb__cell--amount">
                      <span className="nsb__cell-label">Amount</span>
                      <output className="nsb__amount" aria-label={`Line ${i + 1} amount`}>
                        {formatMoney(lineAmount(item), currency)}
                      </output>
                    </div>
                    <LineCell id={id('description')} label="Description" className="nsb__cell--desc">
                      <input
                        id={id('description')}
                        className="input nsb__input"
                        value={item.description}
                        placeholder="Line description"
                        autoComplete="off"
                        onChange={(e) => updateItem(i, { description: e.target.value })}
                      />
                    </LineCell>
                    {suggestions.length > 0 && (
                      <div className="nsb__cell nsb__cell--suggest">
                        <span className="nsb__suggest-label">NetSuite items:</span>
                        {suggestions.map((s) => (
                          <button
                            type="button"
                            key={s.itemId}
                            className={`nsb__suggest${item.itemId === s.itemId ? ' is-selected' : ''}`}
                            onClick={() => updateItem(i, { itemId: s.itemId, itemName: s.name })}
                          >
                            {s.name || 'Item'} · ID {s.itemId}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="nsb__line-remove"
                      onClick={() => removeLine(i)}
                      aria-label={`Remove line ${i + 1}`}
                      title="Remove line"
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ol>
          </>
        ) : (
          <div className={`nsb__lines-empty${errors.items ? ' is-invalid' : ''}`}>
            <p>No line items were found in this document — add them below.</p>
            <p className="nsb__lines-tip">
              Tip: extracting with the “Bill (NetSuite)” document type reads line items most reliably.
            </p>
            {errors.items && <p className="nsb__error">{errors.items}</p>}
          </div>
        )}

        <div className="nsb__lines-foot">
          <button type="button" className="nsb__add-line" onClick={addLine}>
            <span aria-hidden="true">＋</span> Add line
          </button>
          <div className="nsb__sum">
            <div className="nsb__sum-row">
              <span className="nsb__sum-label">Lines total</span>
              <span className="nsb__sum-value">{formatMoney(total, currency)}</span>
            </div>
            {comparison}
          </div>
        </div>
      </section>
    </fieldset>
  );
};

export default NetSuiteBillForm;
